import React from "react";
import { Stack, TextField } from "@mui/material";

import { HeapedCommissionValue } from "../../../../api/commissionHelpers";

/** Treat "" and "-" as empty; otherwise parse and clamp to [0, 100]. */
export function parseClampedPercent(raw: string): number | null {
  if (raw === "" || raw === "-") {
    return null;
  }
  const n = parseFloat(raw);
  return isNaN(n) ? null : Math.max(0, Math.min(100, n));
}

/** Resolve a `HeapedCommissionValue` from the first/thereafter draft strings, or `null` if both are empty. */
export function resolveHeapedValue(
  first: string,
  thereafter: string,
): HeapedCommissionValue | null {
  const firstNum = parseClampedPercent(first);
  const thereafterNum = parseClampedPercent(thereafter);

  if (firstNum === null && thereafterNum === null) return null;

  return { first: firstNum ?? 0, thereafter: thereafterNum ?? 0 };
}

export interface HeapedAmountFieldsProps {
  first: string;
  thereafter: string;
  onFirstChange: (value: string) => void;
  onThereafterChange: (value: string) => void;
}

/**
 * The two clamped-percentage inputs (First Year / Thereafter) shared by
 * `HeapedCommissionEditor` (the plan-grid-core AG Grid editor) and any other
 * surface that needs the same heaped-commission sub-form, e.g. the Comps
 * grid's Edit Commission modal.
 */
export const HeapedAmountFields = ({
  first,
  thereafter,
  onFirstChange,
  onThereafterChange,
}: HeapedAmountFieldsProps): React.JSX.Element => {
  const handleChange =
    (setValue: (value: string) => void) =>
    (e: React.ChangeEvent<HTMLInputElement>): void => {
      const value = e.target.value;
      const numValue = parseFloat(value);
      // Allow empty string for editing, otherwise clamp
      if (value === "" || value === "-") {
        setValue(value);
      } else if (!isNaN(numValue)) {
        setValue(Math.max(0, Math.min(100, numValue)).toString());
      }
    };

  const handleBlur =
    (setValue: (value: string) => void) =>
    (e: React.FocusEvent<HTMLInputElement>): void => {
      const numValue = parseFloat(e.target.value);
      // On blur, allow empty values (no percentage required)
      if (e.target.value === "" || e.target.value === "-") {
        setValue("");
      } else if (isNaN(numValue)) {
        setValue("");
      } else {
        setValue(Math.max(0, Math.min(100, numValue)).toString());
      }
    };

  return (
    <Stack spacing={2}>
      <TextField
        label="First Year (%)"
        type="number"
        value={first}
        onChange={handleChange(onFirstChange)}
        onBlur={handleBlur(onFirstChange)}
        inputProps={{ min: 0, max: 100, step: 0.01 }}
        fullWidth
      />
      <TextField
        label="Thereafter (%)"
        type="number"
        value={thereafter}
        onChange={handleChange(onThereafterChange)}
        onBlur={handleBlur(onThereafterChange)}
        inputProps={{ min: 0, max: 100, step: 0.01 }}
        fullWidth
      />
    </Stack>
  );
};
